import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { PhoneIncoming } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ AUDIO SYSTEM ============
let sharedAudioContext = null;
const ringIntervalsMap = new Map();

const initAudioContext = () => {
  try {
    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (sharedAudioContext.state === 'suspended') {
      sharedAudioContext.resume();
    }
    return sharedAudioContext;
  } catch (error) {
    console.error('[AUDIO] AudioContext init failed:', error);
    return null;
  }
};

const playRingTone = (volume = 0.5) => {
  try {
    const ctx = initAudioContext();
    if (!ctx || ctx.state !== 'running') {
      return false;
    }

    const now = ctx.currentTime;
    
    // First tone - 880Hz
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 880;
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(volume, now + 0.05);
    gain1.gain.linearRampToValueAtTime(volume * 0.8, now + 0.15);
    gain1.gain.linearRampToValueAtTime(0, now + 0.25);
    osc1.start(now);
    osc1.stop(now + 0.25);

    // Second tone - 1100Hz
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1100;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0, now + 0.2);
    gain2.gain.linearRampToValueAtTime(volume, now + 0.25);
    gain2.gain.linearRampToValueAtTime(volume * 0.8, now + 0.4);
    gain2.gain.linearRampToValueAtTime(0, now + 0.55);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.55);

    return true;
  } catch (error) {
    return false;
  }
};

const startRingingForChat = (chatId, volume = 0.5) => {
  if (ringIntervalsMap.has(chatId)) return false;
  
  playRingTone(volume);
  const intervalId = setInterval(() => playRingTone(volume), 3000);
  ringIntervalsMap.set(chatId, intervalId);
  return true;
};

const stopRingingForChat = (chatId) => {
  const intervalId = ringIntervalsMap.get(chatId);
  if (intervalId) {
    clearInterval(intervalId);
    ringIntervalsMap.delete(chatId);
    return true;
  }
  return false;
};

const stopAllRinging = () => {
  ringIntervalsMap.forEach((intervalId) => clearInterval(intervalId));
  ringIntervalsMap.clear();
};

// Volume levels
const VOLUME_LEVELS = { low: 0.2, medium: 0.5, high: 0.8 };

// ============ CONTEXT ============
const LiveChatNotificationContext = createContext(null);

export const useLiveChatNotifications = () => {
  const context = useContext(LiveChatNotificationContext);
  if (!context) {
    throw new Error('useLiveChatNotifications must be used within LiveChatNotificationProvider');
  }
  return context;
};

export const LiveChatNotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  
  // State
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('livechat_sound_enabled') !== 'false';
  });
  const [soundVolume, setSoundVolume] = useState(() => {
    return localStorage.getItem('livechat_sound_volume') || 'medium';
  });
  const [unattendedChats, setUnattendedChats] = useState(new Map());
  const [unattendedCount, setUnattendedCount] = useState(0);
  
  const previousChatIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const pollingIntervalRef = useRef(null);

  // Save preferences
  useEffect(() => {
    localStorage.setItem('livechat_sound_enabled', soundEnabled.toString());
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem('livechat_sound_volume', soundVolume);
  }, [soundVolume]);

  // Update document title
  useEffect(() => {
    const originalTitle = document.title.replace(/^\(\d+\)\s*🔔?\s*/, '').replace(/^New Chat —\s*/, '');
    
    if (unattendedCount > 0) {
      document.title = `(${unattendedCount}) 🔔 New Chat — ${originalTitle}`;
    } else {
      document.title = originalTitle || 'Treppan CRM';
    }
    
    return () => {
      document.title = originalTitle || 'Treppan CRM';
    };
  }, [unattendedCount]);

  // Handle new chat
  const handleNewChat = useCallback((chat) => {
    if (!soundEnabled) return;
    
    const chatId = chat.id;
    const visitorName = chat.visitor_name || 'Visitor';
    const source = chat.source || 'website';
    
    // Add to unattended
    setUnattendedChats(prev => {
      const newMap = new Map(prev);
      newMap.set(chatId, {
        initiatedAt: chat.created_at || new Date().toISOString(),
        visitorName,
        source
      });
      return newMap;
    });
    setUnattendedCount(prev => prev + 1);
    
    // Start ringing
    startRingingForChat(chatId, VOLUME_LEVELS[soundVolume]);
    
    // Toast notification
    toast.info(
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <PhoneIncoming className="text-red-500 animate-pulse" size={16} />
          <span className="font-medium">New chat from {visitorName}</span>
        </div>
        <span className="text-xs text-slate-500">{source}</span>
      </div>,
      {
        id: `chat-${chatId}`,
        duration: Infinity,
        action: {
          label: 'Open',
          onClick: () => {
            window.location.href = `/marketing/live-chat/chats/${chatId}`;
          }
        }
      }
    );
    
    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('🔔 New Live Chat', {
        body: `${visitorName} started a conversation`,
        icon: '/favicon.ico',
        tag: `chat-${chatId}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        window.location.href = `/marketing/live-chat/chats/${chatId}`;
        notification.close();
      };
    }
  }, [soundEnabled, soundVolume]);

  // Handle chat accepted
  const handleChatAccepted = useCallback((chatId) => {
    stopRingingForChat(chatId);
    setUnattendedChats(prev => {
      const newMap = new Map(prev);
      newMap.delete(chatId);
      return newMap;
    });
    setUnattendedCount(prev => Math.max(0, prev - 1));
    toast.dismiss(`chat-${chatId}`);
  }, []);

  // Fetch chats and check for new ones
  const fetchAndCheckChats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/api/live-chat/chats`, {
        params: { token, limit: 50, status: 'open' }
      });
      
      if (!response.data.success) return;
      
      const chats = response.data.chats || [];
      
      if (isFirstLoadRef.current) {
        // First load - identify existing unattended chats
        const existingUnattended = chats.filter(c => c.status === 'open' && !c.assigned_agent_id);
        
        if (existingUnattended.length > 0) {
          const newUnattendedMap = new Map();
          existingUnattended.forEach(chat => {
            newUnattendedMap.set(chat.id, {
              initiatedAt: chat.created_at,
              visitorName: chat.visitor_name || 'Visitor',
              source: chat.source || 'website'
            });
            if (soundEnabled) {
              startRingingForChat(chat.id, VOLUME_LEVELS[soundVolume]);
            }
          });
          setUnattendedChats(newUnattendedMap);
          setUnattendedCount(existingUnattended.length);
        }
        
        previousChatIdsRef.current = new Set(chats.map(c => c.id));
        isFirstLoadRef.current = false;
        
        // Request notification permission
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission();
        }
        return;
      }
      
      // Check for new chats
      const currentIds = new Set(chats.map(c => c.id));
      
      chats.forEach(chat => {
        if (!previousChatIdsRef.current.has(chat.id) && chat.status === 'open' && !chat.assigned_agent_id) {
          handleNewChat(chat);
        }
      });
      
      // Check for claimed chats
      unattendedChats.forEach((_, unattendedChatId) => {
        const chat = chats.find(c => c.id === unattendedChatId);
        if (chat && (chat.status !== 'open' || chat.assigned_agent_id)) {
          handleChatAccepted(unattendedChatId);
        }
      });
      
      previousChatIdsRef.current = currentIds;
      
    } catch (error) {
      console.error('[LIVE_CHAT_NOTIFICATION] Error fetching chats:', error);
    }
  }, [token, soundEnabled, soundVolume, handleNewChat, handleChatAccepted, unattendedChats]);

  // Start polling when authenticated
  useEffect(() => {
    if (!token || !user) return;
    
    // Initial fetch
    fetchAndCheckChats();
    
    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(fetchAndCheckChats, 5000);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      stopAllRinging();
    };
  }, [token, user, fetchAndCheckChats]);

  // Enable sound (user interaction required)
  const enableSound = useCallback(() => {
    const ctx = initAudioContext();
    if (ctx) {
      setSoundEnabled(true);
      playRingTone(VOLUME_LEVELS[soundVolume]);
      toast.success('Sound alerts enabled');
    }
  }, [soundVolume]);

  // Disable sound
  const disableSound = useCallback(() => {
    setSoundEnabled(false);
    stopAllRinging();
  }, []);

  // Test sound
  const testSound = useCallback(() => {
    const ctx = initAudioContext();
    if (ctx) {
      playRingTone(VOLUME_LEVELS[soundVolume]);
      toast.info('Test sound played');
    }
  }, [soundVolume]);

  const value = {
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
    unattendedCount,
    unattendedChats,
    enableSound,
    disableSound,
    testSound,
    handleChatAccepted,
  };

  return (
    <LiveChatNotificationContext.Provider value={value}>
      {children}
    </LiveChatNotificationContext.Provider>
  );
};

export default LiveChatNotificationProvider;
